import { baseApi } from "./baseApi";

export interface Category {
  id: string;
  name: string;
  description?: string;
  image?: string;
  slug: string;
  parentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryData {
  name: string;
  slug: string;
  description?: string;
  image?: string;
  parentId?: string;
}

export interface UpdateCategoryData {
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  parentId?: string;
}

export interface GetCategoriesParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface GetCategoriesResponse {
  success: boolean;
  data: Category[];
  pagination: PaginationInfo;
  message?: string;
}

/**
 * Categories API slice
 * Handles all category-related API calls
 */
export const categoriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all categories
    getCategories: builder.query<
      GetCategoriesResponse,
      GetCategoriesParams | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', params.page.toString());
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.search) searchParams.set('search', params.search);
        const queryString = searchParams.toString();
        return `/categories${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ["Category"],
    }),

    // Get category by ID
    getCategoryById: builder.query<
      { success: boolean; data: Category; message?: string },
      string
    >({
      query: (id) => `/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Category", id }],
    }),

    // Create category
    createCategory: builder.mutation<
      { success: boolean; data: Category; message?: string },
      CreateCategoryData
    >({
      query: (categoryData) => ({
        url: "/categories",
        method: "POST",
        body: categoryData,
      }),
      invalidatesTags: ["Category"],
    }),

    // Update category
    updateCategory: builder.mutation<
      { success: boolean; data: Category; message?: string },
      { id: string; data: UpdateCategoryData }
    >({
      query: ({ id, data }) => ({
        url: `/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Category", id }],
    }),

    // Delete category
    deleteCategory: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = categoriesApi;

